import { z } from 'zod';
/**
 * Dashboard Skill — JSON spec output contract (sections-based, Dashv2 style).
 *
 * 작은 LLM 이 1만+자 HTML 을 직접 짜는 게 아니라, 섹션·위젯 기반 짧은 JSON spec 만 출력하면
 * 서버측 결정론적 렌더러가 폴리시드 HTML 을 생성. open-design / Dashv2 의 vLLM 패턴.
 *
 * Sections-based: 매 대시보드마다 다른 N 개 섹션 + 다른 layout 조합 → 시각적 다양성 확보.
 *
 * LLM 책임: 분석 + spec 결정 (direction / sections [layout/widgets/insight 자유 조합])
 * 서버 책임: spec → HTML (디렉션 OKLch 토큰, 섹션별 layout, 위젯 kind 디스패치, Chart.js, 인터랙션)
 */
/**
 * 비주얼 디렉션 — 5종 큐레이팅된 색상·폰트 세트 (Dashv2 정합).
 * - modern-minimal: Linear / Vercel — 차분한 indigo
 * - editorial: Monocle / FT — 따뜻한 크림 + 테라코타
 * - warm-soft: Stripe pre-2020 / Headspace — 부드러운 코랄
 * - tech-utility: Datadog / GitHub — 민트 / 운영 콕핏
 * - brutalist: Are.na / Yale — 강한 블랙 + 적색 강조
 */
export const DashboardDirectionSchema = z.enum([
    'modern-minimal',
    'editorial',
    'warm-soft',
    'tech-utility',
    'brutalist',
]);
/**
 * 대시보드 intent — "분석 관점". Dashv2 정합 5종.
 *
 * intent 는 sections 구성 의도 (어떤 위젯이 어떤 순서) 자체에 영향. 같은 매출 데이터로도
 * intent 가 다르면 위젯 조합이 본질적으로 달라진다.
 *
 * - executive: 임원 1페이지 요약 — KPI 강조 + 추세 보조. 큰 숫자 중심.
 * - ops: 운영 모니터링 — 실시간 메트릭 + 상태 + 알림 패널.
 * - analytics: 분석 인사이트 — 추세·이상치·상관관계 깊게.
 * - compare: 세그먼트/비교 분석 — 브레이크다운별 성과 비교 강조.
 */
export const DashboardIntentSchema = z.enum(['executive', 'ops', 'analytics', 'compare']);
export const DASHBOARD_DIRECTION_PRESETS = [
    {
        id: 'modern-minimal',
        label: 'Modern Minimal',
        hint: 'Linear / Vercel — 차분한 indigo, B2B/SaaS',
        swatch: [
            'oklch(99% 0.002 240)',
            'oklch(100% 0 0)',
            'oklch(18% 0.012 250)',
            'oklch(54% 0.012 250)',
            'oklch(58% 0.18 255)',
        ],
    },
    {
        id: 'editorial',
        label: 'Editorial',
        hint: 'Monocle / FT — 따뜻한 크림, 매거진/보고',
        swatch: [
            'oklch(97% 0.012 80)',
            'oklch(99% 0.005 80)',
            'oklch(20% 0.02 60)',
            'oklch(48% 0.015 60)',
            'oklch(58% 0.16 35)',
        ],
    },
    {
        id: 'warm-soft',
        label: 'Warm & Soft',
        hint: 'Stripe pre-2020 / Headspace — 부드러운 코랄',
        swatch: [
            'oklch(97% 0.018 70)',
            'oklch(99% 0.008 70)',
            'oklch(22% 0.02 50)',
            'oklch(50% 0.018 50)',
            'oklch(64% 0.13 28)',
        ],
    },
    {
        id: 'tech-utility',
        label: 'Tech & Utility',
        hint: 'Datadog / GitHub — 민트, 운영 콕핏',
        swatch: [
            'oklch(98% 0.005 250)',
            'oklch(100% 0 0)',
            'oklch(22% 0.02 240)',
            'oklch(50% 0.018 240)',
            'oklch(58% 0.16 145)',
        ],
    },
    {
        id: 'brutalist',
        label: 'Brutalist',
        hint: 'Are.na / Yale — 강한 블랙 + 적색 강조',
        swatch: [
            'oklch(96% 0.004 100)',
            'oklch(100% 0 0)',
            'oklch(15% 0.02 100)',
            'oklch(40% 0.02 100)',
            'oklch(60% 0.22 25)',
        ],
    },
];
export const DASHBOARD_INTENT_PRESETS = [
    { id: 'executive', label: '임원 1페이지 요약', hint: 'KPI 강조 + 추세 보조. 큰 숫자 중심' },
    { id: 'ops', label: '운영 모니터링', hint: '실시간 메트릭 + 상태 + 알림 패널' },
    { id: 'analytics', label: '분석 인사이트 중심', hint: '추세·이상치·상관관계 깊게 파헤침' },
    { id: 'compare', label: '세그먼트/비교 분석', hint: '브레이크다운별 성과 비교 강조' },
];
/**
 * archetype — intent 에서 자동 도출되는 시각 위계 패턴 (UI 노출 안 함, 서버 내부용).
 * intent → archetype 매핑: executive→executive, ops→density, analytics→focused, compare→magazine.
 */
export const DashboardArchetypeSchema = z.enum(['executive', 'magazine', 'density', 'focused']);
/** KPI 카드 시각 변형. 같은 archetype 안에서도 카드별로 다른 variant 섞어 단조로움 회피. */
export const KpiVariantSchema = z.enum([
    'default', // 라벨 + 큰 숫자 (현재 기본)
    'accent-bar', // 왼쪽에 4px accent 색 바
    'gradient-hero', // accent 그라데이션 배경 + 흰 글자
    'sparkline', // 라벨 + 큰 숫자 + 우측에 미니 sparkline (시계열 있을 때만)
]);
/** Chart 카드 시각 변형. */
export const ChartVariantSchema = z.enum([
    'default',
    'insight-overlay', // 차트 위에 한 줄 인사이트 오버레이
    'kpi-attached', // 차트 옆에 작은 KPI 한두 개 부착
]);
export const DashboardAggregationSchema = z.enum([
    'sum',
    'avg',
    'count',
    'distinct_count',
    'min',
    'max',
]);
export const DashboardFormatSchema = z.enum([
    'currency-krw',
    'currency-usd',
    'number',
    'percent',
    'count',
    'compact',
]);
export const DashboardChartTypeSchema = z.enum([
    'line',
    'area',
    'bar',
    'horizontal-bar',
    'doughnut',
    'pie',
]);
/** 섹션 layout — Dashv2 의 4종 layout 클래스 + 'full' (단일 위젯). */
export const DashboardSectionLayoutSchema = z.enum([
    'kpi-row', // KPI 카드 한 줄 — 보통 3~6개
    'split', // 2/3 + 1/3 (메인 차트 + 보조)
    'split-equal', // 1/2 + 1/2 (좌우 동일)
    'grid', // 자동 다열 그리드 — 차트 3~6개
    'full', // 단일 위젯 풀폭 (큰 차트 / 큰 표 / 히트맵)
]);
// ---- Widget kinds (discriminated union) -----------------------------------
/** KPI 단일 숫자 카드. */
export const WidgetKpiSchema = z.object({
    kind: z.literal('kpi'),
    label: z.string().min(1).max(60),
    aggregation: DashboardAggregationSchema,
    column: z.string().max(80).optional(),
    format: DashboardFormatSchema.optional(),
    /** 시각 변형. 미지정 시 archetype 기반으로 서버가 자동 분배 (단조로움 회피). */
    variant: KpiVariantSchema.optional(),
});
/** 차트 — line/bar/doughnut 등. */
export const WidgetChartSchema = z.object({
    kind: z.literal('chart'),
    title: z.string().max(80).optional(),
    type: DashboardChartTypeSchema.default('bar'),
    groupByColumn: z.string().min(1).max(80),
    valueColumn: z.string().max(80).optional(),
    aggregation: DashboardAggregationSchema.default('sum'),
    seriesLabel: z.string().max(40).optional(),
    /** 시각 변형. 미지정 시 archetype 기반으로 서버가 자동 분배. */
    variant: ChartVariantSchema.optional(),
    /** kpi-attached variant 에서 옆에 붙이는 보조 KPI. */
    attachedKpi: z
        .object({
        label: z.string().max(40),
        aggregation: DashboardAggregationSchema,
        column: z.string().max(80).optional(),
        format: DashboardFormatSchema.optional(),
    })
        .optional(),
    /** insight-overlay variant 의 오버레이 문구. */
    overlay: z.string().max(120).optional(),
    /** 사용자 커스텀 색상 (5 hex/oklch). 미지정 시 direction 기본 색상 시퀀스. */
    colors: z.array(z.string().max(60)).max(10).optional(),
    /** 시간축 단위 — 시계열 chart 에서. 'auto' 면 데이터 범위 기반 자동. */
    granularity: z.enum(['auto', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly']).optional(),
    /** 정렬 기준 컬럼 / 방향. */
    sortBy: z.enum(['measure', 'dim']).optional(),
    sortDir: z.enum(['asc', 'desc']).optional(),
    /** 상위 N 개 제한. 9999 또는 미지정 시 무제한. */
    topN: z.number().int().min(1).max(9999).optional(),
    /** bar/hbar 의 스택 vs 클러스터 레이아웃. */
    barLayout: z.enum(['stacked', 'clustered']).optional(),
});
/** 표 — 검색 + 정렬 자동. */
export const WidgetTableSchema = z.object({
    kind: z.literal('table'),
    title: z.string().max(80).optional(),
    columns: z
        .array(z.object({
        column: z.string().min(1).max(80),
        label: z.string().min(1).max(60),
        numeric: z.boolean().optional(),
        format: DashboardFormatSchema.optional(),
    }))
        .min(1)
        .max(20),
    defaultSortColumn: z.string().max(80).optional(),
    defaultSortDir: z.enum(['asc', 'desc']).optional(),
    /** 같은 그룹 키의 행을 시각적으로 머지 (rowSpan). */
    merge: z.boolean().optional(),
    /** 그룹 별 소계 행 표시. */
    subtotals: z.boolean().optional(),
    /** 전체 합계 행. */
    grandTotal: z.boolean().optional(),
    /** 페이징 ON/OFF. */
    paginate: z.boolean().optional(),
    /** 페이지당 행 수. */
    pageSize: z.number().int().min(5).max(1000).optional(),
});
/** 히트맵 — 두 차원 매트릭스. */
export const WidgetHeatmapSchema = z.object({
    kind: z.literal('heatmap'),
    title: z.string().max(80).optional(),
    rowColumn: z.string().min(1).max(80),
    colColumn: z.string().min(1).max(80),
    valueColumn: z.string().max(80).optional(),
    aggregation: DashboardAggregationSchema.default('sum'),
});
/** 인사이트 텍스트 박스 — 섹션 안에 분석 코멘트 강조. */
export const WidgetInsightSchema = z.object({
    kind: z.literal('insight'),
    text: z.string().min(1).max(800),
    label: z.string().max(40).optional(),
});
/** Scatter — 두 수치 컬럼의 상관관계 (점 분포). */
export const WidgetScatterSchema = z.object({
    kind: z.literal('scatter'),
    title: z.string().max(80).optional(),
    xColumn: z.string().min(1).max(80),
    yColumn: z.string().min(1).max(80),
    /** 점 색상 그룹핑 (optional). */
    groupByColumn: z.string().max(80).optional(),
});
/** Gauge — 값 vs 목표. 반원 게이지 + 큰 숫자 + 진행률. */
export const WidgetGaugeSchema = z.object({
    kind: z.literal('gauge'),
    label: z.string().min(1).max(60),
    aggregation: DashboardAggregationSchema,
    column: z.string().max(80).optional(),
    /** 목표값 (= 100% 위치). */
    target: z.number(),
    format: DashboardFormatSchema.optional(),
});
/** Distribution — 단일 수치 컬럼의 히스토그램 (분포). */
export const WidgetDistributionSchema = z.object({
    kind: z.literal('distribution'),
    title: z.string().max(80).optional(),
    column: z.string().min(1).max(80),
    /** bin 개수 (3~40). default 15. */
    bins: z.number().int().min(3).max(40).default(15),
});
/** Comparison — 그룹 두 값 비교 (전월/전년/A vs B) + 차이 % + ↗↘. */
export const WidgetComparisonSchema = z.object({
    kind: z.literal('comparison'),
    label: z.string().min(1).max(60),
    /** 비교 차원 (예: '년도' / '브랜드'). 두 값을 자동 추출. */
    groupByColumn: z.string().min(1).max(80),
    valueColumn: z.string().max(80).optional(),
    aggregation: DashboardAggregationSchema,
    format: DashboardFormatSchema.optional(),
    /** base/current 라벨 명시 (없으면 groupBy 의 첫 두 값 자동). */
    baseLabel: z.string().max(40).optional(),
    currentLabel: z.string().max(40).optional(),
});
/** Funnel — 단계별 카운트. groupBy 컬럼이 단계. 단계 순서 명시 가능. */
export const WidgetFunnelSchema = z.object({
    kind: z.literal('funnel'),
    title: z.string().max(80).optional(),
    /** 단계 컬럼 (예: 'stage'). 컬럼의 distinct 값이 각 단계. */
    groupByColumn: z.string().min(1).max(80),
    valueColumn: z.string().max(80).optional(),
    aggregation: DashboardAggregationSchema.default('count'),
    /** 단계 순서 명시. 없으면 값 큰 → 작은 자동 정렬. */
    stageOrder: z.array(z.string()).max(20).optional(),
});
export const WidgetSchema = z.discriminatedUnion('kind', [
    WidgetKpiSchema,
    WidgetChartSchema,
    WidgetTableSchema,
    WidgetHeatmapSchema,
    WidgetInsightSchema,
    WidgetScatterSchema,
    WidgetGaugeSchema,
    WidgetDistributionSchema,
    WidgetComparisonSchema,
    WidgetFunnelSchema,
]);
// ---- Section -------------------------------------------------------------
export const DashboardSectionSchema = z.object({
    /** 섹션 번호 (예: "01"). 표시용, 없으면 자동. */
    number: z.string().max(8).optional(),
    title: z.string().min(1).max(120),
    /** 한 줄 부연 설명 (제목 아래). */
    lede: z.string().max(300).optional(),
    layout: DashboardSectionLayoutSchema,
    widgets: z.array(WidgetSchema).min(1).max(8),
    /** 섹션 하단의 인사이트 박스 (accent 좌측 보더 + tinted bg). */
    insight: z.string().max(600).optional(),
});
/**
 * 다중 탭 — 태블로식 상단 탭 분리. 각 탭이 자체 sections 묶음.
 * 탭 클릭 시 콘텐츠 영역만 교체 (필터/검색은 공통). 첫 탭이 default 활성.
 *
 * spec.tabs 가 있으면 spec.sections 는 무시. 없으면 spec.sections 가 단일 탭으로 렌더.
 */
export const DashboardTabSchema = z.object({
    id: z.string().min(1).max(60),
    label: z.string().min(1).max(60),
    /** 탭 안의 섹션들. 일반 sections 와 동일 schema. */
    sections: z.array(DashboardSectionSchema).min(1).max(8),
});
export const DashboardFilterDropdownSchema = z.object({
    column: z.string().min(1).max(80),
    label: z.string().min(1).max(40),
});
export const DashboardSpecSchema = z.object({
    /** 페이지 제목. */
    title: z.string().min(1).max(200),
    /** 부제 / 기간 표시. */
    subtitle: z.string().max(200).optional(),
    /** 카테고리 kicker. */
    kicker: z.string().max(60).optional(),
    /**
     * 비주얼 디렉션 (3색 세트 — Indigo Pro / Sunset Editorial / Forest Pro).
     *
     * 사용자가 ask_user 응답으로 명시 선택한 경우에만 채움. 미정이면 생략 — 서버 핸들러가
     * 자동으로 색상 선택 인터럽트 (swatch 포함 ask_user) 를 띄워 사용자에게 컨펌 받는다.
     * 즉 LLM 이 임의로 추측해 채우지 않는 게 깔끔. (자연어 텍스트로만 "톤 선택해 주세요"
     * 라고 안내한 뒤 곧장 대시보드 생성으로 직진하는 패턴 차단.)
     */
    direction: DashboardDirectionSchema.optional(),
    /**
     * 분석 관점 intent — 4가지 + AI 자체 판단.
     * 미정이면 서버 핸들러가 ask_user 로 사용자에게 묻는다. archetype 은 intent 에서 자동 도출.
     */
    intent: DashboardIntentSchema.optional(),
    /**
     * archetype — intent 에서 자동 도출됨. 사용자가 직접 채우지 않는 게 원칙.
     * 호환성을 위해 optional 로 남겨두되, intent 가 있으면 intent 가 우선.
     */
    archetype: DashboardArchetypeSchema.optional(),
    /** db.save_query / excel.save_query 가 반환한 saved query UUID. */
    queryId: z.string().uuid(),
    /**
     * 다중 탭 — 태블로식. tabs 가 있으면 sections 는 무시.
     * tabs 없으면 sections 가 단일 탭으로 렌더 (default).
     */
    tabs: z.array(DashboardTabSchema).max(8).optional(),
    /** 2~7 개 섹션 (tabs 미사용 시). 매 대시보드마다 다른 구성을 권장. */
    sections: z.array(DashboardSectionSchema).min(1).max(8).optional(),
    /** 상단 필터 (모든 섹션에 적용). */
    filters: z
        .object({
        dateColumn: z.string().max(80).optional(),
        dropdowns: z.array(DashboardFilterDropdownSchema).max(6).default([]),
        showSearch: z.boolean().default(true),
    })
        .default({}),
});
/**
 * spec → 실제 쿼리 컬럼 정합 검사 결과. LLM 이 환각 컬럼을 채웠거나 저장 쿼리가 해당 컬럼을
 * 집계로 날려버린 경우, 서버가 위젯 컬럼을 드롭한다. 모델이 이 사실을 알고 스스로 쿼리를
 * 고치도록 dashboard.create 응답에 실어 보낸다. 드롭이 없으면 생략.
 */
export const DashboardCoercionReportSchema = z.object({
    /** 저장 쿼리 결과에 없어 위젯에서 제거된 컬럼들. */
    droppedColumns: z.array(z.object({ widget: z.string(), column: z.string() })),
    /** 컬럼이 모두 제거되어 통째로 빠진 위젯 수. */
    droppedWidgetCount: z.number().int(),
    /** 저장 쿼리 결과의 실제 컬럼 목록 — 모델이 쿼리를 다시 짤 때 참고. */
    availableColumns: z.array(z.string()),
    /**
     * 데이터 형태가 받쳐주지 않아 제거된 위젯들 (컬럼은 멀쩡하지만 통계적으로 무의미한 경우).
     * 예: 2~3 행짜리 사전집계 요약 위의 distribution/scatter — 히스토그램 Y축이 0~1 이 되거나
     * 산점도 점이 2개뿐이라 생성하지 않는다. 모델이 "더 granular 한 쿼리를 저장"하도록 사유 노출.
     */
    droppedUnsuitable: z.array(z.object({ widget: z.string(), reason: z.string() })).optional(),
});
/** dashboard.create 툴 호출 응답. */
export const DashboardCreateOutputSchema = z.object({
    artifactId: z.string().uuid(),
    title: z.string(),
    /** 새로 만든 경우 1, 같은 title 의 직전 dashboard 가 있어 append 된 경우 2, 3, ... */
    version: z.number().int().min(1),
    applied: z.object({
        direction: DashboardDirectionSchema,
        intent: DashboardIntentSchema,
        archetype: DashboardArchetypeSchema,
        tabCount: z.number().int(),
        sectionCount: z.number().int(),
        widgetCount: z.number().int(),
    }),
    /** 위젯 컬럼이 저장 쿼리 결과와 어긋나 드롭된 경우의 리포트. 드롭이 없으면 생략. */
    coercion: DashboardCoercionReportSchema.optional(),
});
//# sourceMappingURL=dashboard.js.map